import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import type {
  IAiSpreadsheetRegionInput,
  IAiSpreadsheetRegionOverrideCandidate,
  IAiSpreadsheetRegionOverrideConfirmResponse,
  IAiSpreadsheetRegionResponse,
} from "@/payroll/ai-script/types";
import {
  getAiScriptSpreadsheetRegionOverrideConfirmPath,
  getAiScriptSpreadsheetRegionPath,
  getAiScriptSpreadsheetRegionsPath,
} from "./paths";
import {
  patchAiSpreadsheetRegionCaches,
  syncConfirmedAiSpreadsheetRegionOverrideCaches,
} from "./region-caches";

export interface CreateAiSpreadsheetRegionParams {
  runId: string;
  sheetId: string;
  region: IAiSpreadsheetRegionInput;
}

export interface UpdateAiSpreadsheetRegionParams
  extends CreateAiSpreadsheetRegionParams {
  regionId: string;
}

export interface DeleteAiSpreadsheetRegionParams {
  runId: string;
  sheetId: string;
  regionId: string;
}

export interface ConfirmAiSpreadsheetRegionOverrideParams {
  runId: string;
  sheetId: string;
  overrideId: string;
  candidate: IAiSpreadsheetRegionOverrideCandidate;
}

export const postAiScriptSpreadsheetRegion = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, region }: CreateAiSpreadsheetRegionParams,
) =>
  api
    .post(getAiScriptSpreadsheetRegionsPath(businessId, runId, sheetId), {
      confirmed: true,
      region,
    })
    .then((res) => res.data as IAiSpreadsheetRegionResponse);

export const patchAiScriptSpreadsheetRegion = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, regionId, region }: UpdateAiSpreadsheetRegionParams,
) =>
  api
    .patch(
      getAiScriptSpreadsheetRegionPath(businessId, runId, sheetId, regionId),
      { region },
    )
    .then((res) => res.data as IAiSpreadsheetRegionResponse);

export const deleteAiScriptSpreadsheetRegion = (
  api: AxiosInstance,
  businessId: string,
  { runId, sheetId, regionId }: DeleteAiSpreadsheetRegionParams,
) =>
  api.delete(
    getAiScriptSpreadsheetRegionPath(businessId, runId, sheetId, regionId),
  );

export const useCreateAiSpreadsheetRegion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateAiSpreadsheetRegionParams) => {
      if (!businessId) throw new Error("Business is required");
      return postAiScriptSpreadsheetRegion(api, businessId, params);
    },
    onSuccess: ({ region }, { runId, sheetId }) => {
      patchAiSpreadsheetRegionCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        (regions) => [...regions, region],
      );
    },
  });
};

export const useUpdateAiSpreadsheetRegion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateAiSpreadsheetRegionParams) => {
      if (!businessId) throw new Error("Business is required");
      return patchAiScriptSpreadsheetRegion(api, businessId, params);
    },
    onSuccess: ({ region }, { runId, sheetId }) => {
      patchAiSpreadsheetRegionCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        (regions) =>
          regions.map((current) =>
            current.id === region.id ? region : current,
          ),
      );
    },
  });
};

export const useDeleteAiSpreadsheetRegion = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteAiSpreadsheetRegionParams) => {
      if (!businessId) throw new Error("Business is required");
      await deleteAiScriptSpreadsheetRegion(api, businessId, params);
    },
    onSuccess: (_response, { runId, sheetId, regionId }) => {
      patchAiSpreadsheetRegionCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        (regions) => regions.filter((region) => region.id !== regionId),
      );
    },
  });
};

export const postAiScriptSpreadsheetRegionOverrideConfirm = (
  api: AxiosInstance,
  businessId: string,
  { runId, overrideId, candidate }: ConfirmAiSpreadsheetRegionOverrideParams,
) =>
  api
    .post(
      getAiScriptSpreadsheetRegionOverrideConfirmPath(
        businessId,
        runId,
        overrideId,
      ),
      {
        candidate: {
          header_row: candidate.header_row,
          start_col: candidate.start_col,
        },
      },
    )
    .then((res) => res.data as IAiSpreadsheetRegionOverrideConfirmResponse);

export const useConfirmAiSpreadsheetRegionOverride = (
  businessId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ConfirmAiSpreadsheetRegionOverrideParams) => {
      if (!businessId) throw new Error("Business is required");
      return postAiScriptSpreadsheetRegionOverrideConfirm(
        api,
        businessId,
        params,
      );
    },
    onSuccess: ({ region }, { runId, sheetId, overrideId }) =>
      syncConfirmedAiSpreadsheetRegionOverrideCaches(
        queryClient,
        businessId,
        runId,
        sheetId,
        overrideId,
        region,
      ),
  });
};
